import { useState, useCallback, useEffect, useMemo } from 'react';
import type {
  DataTableColumn,
  DataTablePersistedState,
  SortConfig,
  FilterValue,
  ViewMode,
  NamedView,
  SavedViews,
} from '../types';

const STORAGE_PREFIX = 'datatable:';
const VIEWS_SUFFIX = ':views';

interface UseDataTableStateOptions<T> {
  tableId: string;
  columns: DataTableColumn<T>[];
  defaultView?: ViewMode;
}

interface UseDataTableStateReturn {
  // Column state
  columnOrder: string[];
  columnWidths: Record<string, number>;
  hiddenColumns: Set<string>;
  setColumnOrder: (order: string[]) => void;
  setColumnWidth: (column: string, width: number) => void;
  toggleColumnVisibility: (column: string) => void;

  // Sort state
  sortConfig: SortConfig | null;
  setSortConfig: (config: SortConfig | null) => void;
  toggleSort: (column: string) => void;

  // Filter state
  filters: Record<string, FilterValue>;
  setFilter: (column: string, value: FilterValue | null) => void;
  clearFilters: () => void;
  activeFilterCount: number;

  // View mode
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;

  // Named views
  savedViews: NamedView[];
  activeViewId: string;
  saveView: (name: string) => string; // Returns the new view ID
  loadView: (viewId: string) => void;
  deleteView: (viewId: string) => void;
  renameView: (viewId: string, newName: string) => void;

  // Reset
  resetState: () => void;
}

function loadState(tableId: string): Partial<DataTablePersistedState> | null {
  try {
    const stored = localStorage.getItem(`${STORAGE_PREFIX}${tableId}:state`);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function saveState(tableId: string, state: DataTablePersistedState): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${tableId}:state`, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}

function loadSavedViews(tableId: string): SavedViews {
  try {
    const stored = localStorage.getItem(`${STORAGE_PREFIX}${tableId}${VIEWS_SUFFIX}`);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore errors
  }
  return { activeViewId: 'default', views: [] };
}

function saveSavedViews(tableId: string, savedViews: SavedViews): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${tableId}${VIEWS_SUFFIX}`, JSON.stringify(savedViews));
  } catch {
    // Ignore storage errors
  }
}

function generateViewId(): string {
  return `view_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function useDataTableState<T>({
  tableId,
  columns,
  defaultView = 'table',
}: UseDataTableStateOptions<T>): UseDataTableStateReturn {
  // Load initial state from localStorage
  const initialState = useMemo(() => loadState(tableId), [tableId]);
  const initialViews = useMemo(() => loadSavedViews(tableId), [tableId]);

  // Default column order from columns prop
  const defaultColumnOrder = useMemo(
    () => columns.map((c) => c.key),
    [columns]
  );

  // Column order
  const [columnOrder, setColumnOrderState] = useState<string[]>(
    initialState?.columnOrder ?? defaultColumnOrder
  );

  // Column widths
  const [columnWidths, setColumnWidthsState] = useState<Record<string, number>>(
    initialState?.columnWidths ?? {}
  );

  // Hidden columns
  const [hiddenColumns, setHiddenColumnsState] = useState<Set<string>>(
    new Set(initialState?.hiddenColumns ?? columns.filter((c) => c.hidden).map((c) => c.key))
  );

  // Sort config
  const [sortConfig, setSortConfigState] = useState<SortConfig | null>(
    initialState?.sortConfig ?? null
  );

  // Filters
  const [filters, setFiltersState] = useState<Record<string, FilterValue>>(
    initialState?.filters ?? {}
  );

  // View mode
  const [viewMode, setViewModeState] = useState<ViewMode>(
    initialState?.viewMode ?? defaultView
  );

  // Named views state
  const [savedViews, setSavedViewsState] = useState<NamedView[]>(initialViews.views);
  const [activeViewId, setActiveViewId] = useState<string>(initialViews.activeViewId);

  // Persist state on changes
  useEffect(() => {
    const state: DataTablePersistedState = {
      columnOrder,
      columnWidths,
      hiddenColumns: Array.from(hiddenColumns),
      sortConfig,
      filters,
      viewMode,
    };
    saveState(tableId, state);
  }, [tableId, columnOrder, columnWidths, hiddenColumns, sortConfig, filters, viewMode]);

  // Persist saved views on changes
  useEffect(() => {
    saveSavedViews(tableId, { activeViewId, views: savedViews });
  }, [tableId, activeViewId, savedViews]);

  // Column order
  const setColumnOrder = useCallback((order: string[]) => {
    setColumnOrderState(order);
  }, []);

  // Column width
  const setColumnWidth = useCallback((column: string, width: number) => {
    setColumnWidthsState((prev) => ({ ...prev, [column]: width }));
  }, []);

  // Toggle column visibility
  const toggleColumnVisibility = useCallback((column: string) => {
    setHiddenColumnsState((prev) => {
      const next = new Set(prev);
      if (next.has(column)) {
        next.delete(column);
      } else {
        next.add(column);
      }
      return next;
    });
  }, []);

  // Sort config
  const setSortConfig = useCallback((config: SortConfig | null) => {
    setSortConfigState(config);
  }, []);

  // Toggle sort (cycles: none -> asc -> desc -> none)
  const toggleSort = useCallback((column: string) => {
    setSortConfigState((prev) => {
      if (!prev || prev.column !== column) {
        return { column, order: 'asc' };
      }
      if (prev.order === 'asc') {
        return { column, order: 'desc' };
      }
      return null;
    });
  }, []);

  // Set filter
  const setFilter = useCallback((column: string, value: FilterValue | null) => {
    setFiltersState((prev) => {
      if (value === null) {
        const { [column]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [column]: value };
    });
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFiltersState({});
  }, []);

  // Active filter count
  const activeFilterCount = useMemo(() => Object.keys(filters).length, [filters]);

  // View mode
  const setViewMode = useCallback((mode: ViewMode) => {
    setViewModeState(mode);
  }, []);

  // Reset all state
  const resetState = useCallback(() => {
    setColumnOrderState(defaultColumnOrder);
    setColumnWidthsState({});
    setHiddenColumnsState(new Set(columns.filter((c) => c.hidden).map((c) => c.key)));
    setSortConfigState(null);
    setFiltersState({});
    setViewModeState(defaultView);
    setActiveViewId('default');
    localStorage.removeItem(`${STORAGE_PREFIX}${tableId}:state`);
  }, [tableId, defaultColumnOrder, columns, defaultView]);

  // Save current state as a named view
  const saveView = useCallback((name: string): string => {
    const viewId = generateViewId();
    const newView: NamedView = {
      id: viewId,
      name,
      createdAt: new Date().toISOString(),
      state: {
        columnOrder,
        columnWidths,
        hiddenColumns: Array.from(hiddenColumns),
        sortConfig,
        filters,
        viewMode,
      },
    };
    setSavedViewsState((prev) => [...prev, newView]);
    setActiveViewId(viewId);
    return viewId;
  }, [columnOrder, columnWidths, hiddenColumns, sortConfig, filters, viewMode]);

  // Load a saved view
  const loadView = useCallback((viewId: string) => {
    if (viewId === 'default') {
      resetState();
      return;
    }
    const view = savedViews.find((v) => v.id === viewId);
    if (!view) return;

    setColumnOrderState(view.state.columnOrder);
    setColumnWidthsState(view.state.columnWidths);
    setHiddenColumnsState(new Set(view.state.hiddenColumns));
    setSortConfigState(view.state.sortConfig);
    setFiltersState(view.state.filters);
    setViewModeState(view.state.viewMode);
    setActiveViewId(viewId);
  }, [savedViews, resetState]);

  // Delete a saved view
  const deleteView = useCallback((viewId: string) => {
    setSavedViewsState((prev) => prev.filter((v) => v.id !== viewId));
    if (activeViewId === viewId) {
      setActiveViewId('default');
    }
  }, [activeViewId]);

  // Rename a saved view
  const renameView = useCallback((viewId: string, newName: string) => {
    setSavedViewsState((prev) =>
      prev.map((v) => (v.id === viewId ? { ...v, name: newName } : v))
    );
  }, []);

  return {
    columnOrder,
    columnWidths,
    hiddenColumns,
    setColumnOrder,
    setColumnWidth,
    toggleColumnVisibility,
    sortConfig,
    setSortConfig,
    toggleSort,
    filters,
    setFilter,
    clearFilters,
    activeFilterCount,
    viewMode,
    setViewMode,
    savedViews,
    activeViewId,
    saveView,
    loadView,
    deleteView,
    renameView,
    resetState,
  };
}
